import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box, Button, TextField, Typography, Link, 
  CircularProgress, Alert
} from '@mui/material';
import api from '../../services/api';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        
        // Call the forgot-password API endpoint
        await api.post('/api/auth/forgot-password', values);
        
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to send password reset email');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box sx={{ position: 'relative', mt: 3 }}>
      <Typography component="h1" variant="h5" align="center"
        sx={{ 
          fontWeight: 'bold', 
          color: (theme) => theme.palette.primary.main,
          mb: 3
        }}>
        Reset Password
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            Password reset instructions sent to your email!
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link 
              component={RouterLink} 
              to="/login" 
              variant="body2"
              sx={{ 
                color: theme => theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to login
            </Link>
          </Box>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Enter your email address and we'll send you instructions to reset your password.
          </Typography>
          
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            type="email"
            margin="normal"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            disabled={loading}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          
          <Button
            fullWidth
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            sx={{ 
              mt: 4, 
              mb: 2, 
              py: 1.5, 
              borderRadius: 2,
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.20)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
          </Button>
          
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link 
              component={RouterLink} 
              to="/login" 
              variant="body2"
              sx={{ 
                color: theme => theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to login
            </Link>
          </Box>
        </form>
      )}
    </Box>
  );
};

export default ForgotPassword;